import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
  IconButton,
  Dialog,
  DialogContent,
  LinearProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Tooltip,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  EventNote as EventNoteIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Warning as WarningIcon,
  CameraAlt as CameraAltIcon,
  Send as SendIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  PersonOff as PersonOffIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  TaskAlt as ReviewIcon,
  AccessTime as AccessTimeIcon,
  Construction as ConstructionIcon,
  Inventory as InventoryIcon,
  Block as BlockIcon,
  FilterAlt as FilterAltIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import VoiceTranscribe from '../components/VoiceTranscribe';
import {
  getDailyReports,
  createDailyReport,
  updateDailyReport,
  submitDailyReport,
  reviewDailyReport,
  uploadReportPhotos,
  getDailyReportSummary,
  getDailyReportMyProjects,
  getDailyReportsRecent,
  previewAutoDailyReport,
  autoGenerateDailyReport,
  getProjects, // eslint-disable-line no-unused-vars
  getUsers,
} from '../services/api';

const ACS_BLUE = '#0D2C54';
const STATUS_COLORS = {
  draft: 'default',
  submitted: 'primary',
  reviewed: 'success',
};
const STATUS_LABELS = {
  draft: 'Draft',
  submitted: 'Submitted',
  reviewed: 'Reviewed',
};

// ── Empty form state ──────────────────────────────────────────────────────────
const emptyForm = () => ({
  hours_worked: '',
  arrival_time: '',
  departure_time: '',
  tasks_completed: '',
  tasks_in_progress: '',
  blockers: '',
  materials_used: '',
  materials_needed: '',
  percent_complete: 0,
  safety_concerns: '',
  phase_id: '',
});

// ── Reusable voice-enabled text field ─────────────────────────────────────────
function VoiceTextField({ projectId, label, value, onChange, rows = 3, placeholder, icon }) {
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {icon && React.cloneElement(icon, { fontSize: 'small', sx: { color: 'text.secondary' } })}
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {label}
          </Typography>
        </Box>
        {projectId && (
          <VoiceTranscribe
            projectId={projectId}
            label="Voice"
            compact={false}
            onTranscribed={(text) =>
              onChange({ target: { value: value ? `${value}\n${text}` : text } })
            }
          />
        )}
      </Box>
      <TextField
        fullWidth
        multiline
        rows={rows}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        size="small"
        sx={{ '& .MuiInputBase-root': { minHeight: 48 } }}
      />
    </Box>
  );
}

// ── Photo gallery / upload ────────────────────────────────────────────────────
function PhotoSection({ reportId, photos, onPhotosUpdated, readOnly }) {
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null); // url string

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach((f) => formData.append('photos', f));
      const res = await uploadReportPhotos(reportId, formData);
      onPhotosUpdated(res.data.photos);
    } catch (err) {
      console.error('Photo upload error:', err);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CameraAltIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            PHOTOS ({photos.length})
          </Typography>
        </Box>
        {!readOnly && (
          <Button
            component="label"
            size="small"
            variant="outlined"
            startIcon={uploading ? <CircularProgress size={14} /> : <CameraAltIcon />}
            disabled={uploading || !reportId}
            sx={{ minHeight: 36 }}
          >
            Add Photos
            <input
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              hidden
              onChange={handleFileChange}
            />
          </Button>
        )}
      </Box>

      {photos.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {photos.map((p, idx) => (
            <Box
              key={idx}
              sx={{ position: 'relative', cursor: 'pointer' }}
              onClick={() => setLightbox(p.url)}
            >
              <Box
                component="img"
                src={p.url}
                alt={p.caption || `Photo ${idx + 1}`}
                sx={{
                  width: 80,
                  height: 80,
                  objectFit: 'cover',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              />
              {p.caption && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    maxWidth: 80,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: '0.65rem',
                    color: 'text.secondary',
                  }}
                >
                  {p.caption}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Lightbox */}
      <Dialog open={Boolean(lightbox)} onClose={() => setLightbox(null)} maxWidth="lg">
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.5)', color: 'white' }}
            onClick={() => setLightbox(null)}
          >
            <CloseIcon />
          </IconButton>
          {lightbox && (
            <Box
              component="img"
              src={lightbox}
              alt="Full size"
              sx={{ maxWidth: '90vw', maxHeight: '90vh', display: 'block' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

// ── Report Form ───────────────────────────────────────────────────────────────
function ReportForm({ project, existingReport, onSaved, onSubmitted }) {
  const [form, setForm] = useState(() => {
    if (existingReport) {
      return {
        hours_worked: existingReport.hours_worked ?? '',
        arrival_time: existingReport.arrival_time ?? '',
        departure_time: existingReport.departure_time ?? '',
        tasks_completed: existingReport.tasks_completed ?? '',
        tasks_in_progress: existingReport.tasks_in_progress ?? '',
        blockers: existingReport.blockers ?? '',
        materials_used: existingReport.materials_used ?? '',
        materials_needed: existingReport.materials_needed ?? '',
        percent_complete: existingReport.percent_complete ?? 0,
        safety_concerns: existingReport.safety_concerns ?? '',
        phase_id: existingReport.phase_id ?? '',
      };
    }
    return emptyForm();
  });

  const [report, setReport] = useState(existingReport || null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [photos, setPhotos] = useState(existingReport?.photos || []);
  const [autoFilling, setAutoFilling] = useState(false);
  const [autoMeta, setAutoMeta] = useState(null);

  const isReadOnly = report && (report.status === 'submitted' || report.status === 'reviewed');

  // ── Auto-fill from today's actual work (Field Ops v1 #3) ──────────────────────
  const handleAutoFill = async (overwrite = false) => {
    setError('');
    setAutoFilling(true);
    try {
      const reportDate = new Date().toISOString().split('T')[0];
      const res = await autoGenerateDailyReport({
        project_id: project.id,
        report_date: reportDate,
        overwrite,
      });
      const saved = res.data.report;
      setReport(saved);
      setAutoMeta(res.data.meta);
      setForm({
        hours_worked: saved.hours_worked ?? '',
        arrival_time: saved.arrival_time ?? '',
        departure_time: saved.departure_time ?? '',
        tasks_completed: saved.tasks_completed ?? '',
        tasks_in_progress: saved.tasks_in_progress ?? '',
        blockers: saved.blockers ?? '',
        materials_used: saved.materials_used ?? '',
        materials_needed: saved.materials_needed ?? '',
        percent_complete: saved.percent_complete ?? 0,
        safety_concerns: saved.safety_concerns ?? '',
        phase_id: saved.phase_id ?? '',
      });
      setPhotos(saved.photos || []);
      onSaved && onSaved(saved);
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data || {};
      if (status === 422) {
        setError(
          'No completed tasks recorded for this project today — fill in the report manually.'
        );
      } else if (status === 409) {
        // Confirm overwrite
        if (
          window.confirm(
            `${data.error || 'A report already exists.'}\n\nReplace it with the auto-generated version?`
          )
        ) {
          await handleAutoFill(true);
          return;
        }
      } else {
        setError(data.error || 'Auto-fill failed');
      }
    } finally {
      setAutoFilling(false);
    }
  };

  const field = (key) => ({
    value: form[key],
    onChange: (e) => setForm((f) => ({ ...f, [key]: e.target.value })),
  });

  const handleSave = async () => {
    setError('');
    setSaving(true);
    try {
      if (!form.hours_worked && form.hours_worked !== 0) {
        setError('Hours worked is required');
        setSaving(false);
        return;
      }
      let saved;
      if (report) {
        const res = await updateDailyReport(report.id, form);
        saved = res.data;
      } else {
        const res = await createDailyReport({
          ...form,
          project_id: project.id,
          report_date: new Date().toISOString().split('T')[0],
        });
        saved = res.data;
      }
      setReport(saved);
      onSaved && onSaved(saved);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to save report';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  // Two-step submit when report hasn't been saved yet
  const handleSaveAndSubmit = async () => {
    setError('');
    setSaving(true);
    try {
      if (!form.hours_worked && form.hours_worked !== 0) {
        setError('Hours worked is required');
        setSaving(false);
        return;
      }
      let saved;
      if (report) {
        const res = await updateDailyReport(report.id, form);
        saved = res.data;
      } else {
        const res = await createDailyReport({
          ...form,
          project_id: project.id,
          report_date: new Date().toISOString().split('T')[0],
        });
        saved = res.data;
      }
      setReport(saved);
      // Now submit
      setSubmitting(true);
      const res2 = await submitDailyReport(saved.id);
      setReport(res2.data);
      onSubmitted && onSubmitted(res2.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save/submit');
    } finally {
      setSaving(false);
      setSubmitting(false);
    }
  };

  return (
    <Box>
      {/* Status bar */}
      {report && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={STATUS_LABELS[report.status] || report.status}
            color={STATUS_COLORS[report.status] || 'default'}
            size="small"
          />
          {report.submitted_at && (
            <Typography variant="caption" color="text.secondary">
              Submitted {new Date(report.submitted_at).toLocaleTimeString()}
            </Typography>
          )}
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Auto-fill banner (Field Ops v1 #3) */}
      {!isReadOnly && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'primary.light',
            bgcolor: 'primary.lighter',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} sx={{ color: ACS_BLUE }}>
              Auto-fill from today's work
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {autoMeta
                ? `${autoMeta.counts.completed_tasks} completed, ${autoMeta.counts.in_progress_tasks} in progress, ` +
                  `${autoMeta.counts.blocked_tasks} blocked, ${autoMeta.counts.photos} photos, ` +
                  `${autoMeta.counts.os_confirmed} OS confirmed — review and submit.`
                : 'Pulls completed tasks, photos, notes, and time tracking from your field entries today.'}
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            onClick={() => handleAutoFill(false)}
            disabled={autoFilling}
            sx={{ minWidth: 140 }}
          >
            {autoFilling ? 'Building…' : autoMeta ? 'Refresh' : 'Auto-fill'}
          </Button>
        </Box>
      )}

      {/* Time tracking */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <AccessTimeIcon fontSize="small" /> TIME TRACKING
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={4}>
            <TextField
              label="Hours Worked *"
              type="number"
              size="small"
              fullWidth
              inputProps={{ min: 0, max: 24, step: 0.5 }}
              {...field('hours_worked')}
              disabled={isReadOnly}
              sx={{ '& .MuiInputBase-root': { minHeight: 48 } }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Arrival"
              type="time"
              size="small"
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              {...field('arrival_time')}
              disabled={isReadOnly}
              sx={{ '& .MuiInputBase-root': { minHeight: 48 } }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Departure"
              type="time"
              size="small"
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              {...field('departure_time')}
              disabled={isReadOnly}
              sx={{ '& .MuiInputBase-root': { minHeight: 48 } }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Percent complete */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            PROJECT % COMPLETE
          </Typography>
          <Chip
            label={`${form.percent_complete ?? 0}%`}
            size="small"
            color={
              form.percent_complete >= 100 ? 'success'
              : form.percent_complete >= 50 ? 'primary'
              : 'default'
            }
          />
        </Box>
        <Slider
          value={form.percent_complete ?? 0}
          onChange={(_, v) => setForm((f) => ({ ...f, percent_complete: v }))}
          min={0}
          max={100}
          step={5}
          marks={[{ value: 0 }, { value: 25 }, { value: 50 }, { value: 75 }, { value: 100 }]}
          disabled={isReadOnly}
          sx={{ color: ACS_BLUE }}
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Work fields */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <VoiceTextField
          projectId={project?.id}
          label="TASKS COMPLETED TODAY"
          rows={3}
          placeholder="What did you accomplish today?"
          icon={<CheckCircleIcon />}
          {...field('tasks_completed')}
          onChange={field('tasks_completed').onChange}
          value={form.tasks_completed}
          disabled={isReadOnly}
        />
        <VoiceTextField
          projectId={project?.id}
          label="WORK IN PROGRESS"
          rows={2}
          placeholder="What's still in progress?"
          icon={<HourglassEmptyIcon />}
          {...field('tasks_in_progress')}
          onChange={field('tasks_in_progress').onChange}
          value={form.tasks_in_progress}
          disabled={isReadOnly}
        />
        <VoiceTextField
          projectId={project?.id}
          label="BLOCKERS / ISSUES"
          rows={2}
          placeholder="Anything blocking progress?"
          icon={<BlockIcon />}
          {...field('blockers')}
          onChange={field('blockers').onChange}
          value={form.blockers}
          disabled={isReadOnly}
        />
        <VoiceTextField
          projectId={project?.id}
          label="MATERIALS USED"
          rows={2}
          placeholder="Materials consumed today"
          icon={<InventoryIcon />}
          {...field('materials_used')}
          onChange={field('materials_used').onChange}
          value={form.materials_used}
          disabled={isReadOnly}
        />
        <VoiceTextField
          projectId={project?.id}
          label="MATERIALS NEEDED FOR NEXT VISIT"
          rows={2}
          placeholder="What do you need next time?"
          icon={<ConstructionIcon />}
          {...field('materials_needed')}
          onChange={field('materials_needed').onChange}
          value={form.materials_needed}
          disabled={isReadOnly}
        />
        <VoiceTextField
          projectId={project?.id}
          label="SAFETY CONCERNS"
          rows={2}
          placeholder="Any safety observations or concerns"
          icon={<WarningIcon />}
          {...field('safety_concerns')}
          onChange={field('safety_concerns').onChange}
          value={form.safety_concerns}
          disabled={isReadOnly}
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Photos */}
      <PhotoSection
        reportId={report?.id}
        photos={photos}
        onPhotosUpdated={setPhotos}
        readOnly={isReadOnly}
      />

      {/* Action buttons */}
      {!isReadOnly && (
        <Box sx={{ mt: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || submitting}
            sx={{ minHeight: 48, flex: 1 }}
          >
            Save Draft
          </Button>
          <Button
            variant="contained"
            startIcon={submitting ? <CircularProgress size={16} /> : <SendIcon />}
            onClick={handleSaveAndSubmit}
            disabled={saving || submitting}
            sx={{ minHeight: 48, flex: 1, bgcolor: ACS_BLUE }}
          >
            Submit Report
          </Button>
        </Box>
      )}

      {isReadOnly && report?.status === 'submitted' && (
        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mt: 2 }}>
          Report submitted successfully. A manager will review it shortly.
        </Alert>
      )}
      {isReadOnly && report?.status === 'reviewed' && (
        <Alert severity="info" icon={<ReviewIcon />} sx={{ mt: 2 }}>
          Report reviewed by {report.reviewer_name} on{' '}
          {report.reviewed_at ? new Date(report.reviewed_at).toLocaleString() : '—'}.
        </Alert>
      )}
    </Box>
  );
}

// ── Project card for field staff ──────────────────────────────────────────────
function ProjectCard({ project, todayReport, onReportChange }) {
  const [expanded, setExpanded] = useState(false);
  const [report, setReport] = useState(todayReport);
  const [toast, setToast] = useState('');

  const handleSaved = (saved) => {
    setReport(saved);
    setToast('Draft saved');
    onReportChange && onReportChange(saved);
  };

  const handleSubmitted = (submitted) => {
    setReport(submitted);
    setToast('Report submitted!');
    onReportChange && onReportChange(submitted);
  };

  const statusColor = report
    ? STATUS_COLORS[report.status] || 'default'
    : 'default';

  return (
    <>
      <Card
        sx={{
          mb: 2,
          border: report ? `2px solid` : '1px solid',
          borderColor: report
            ? (report.status === 'reviewed' ? 'success.main'
              : report.status === 'submitted' ? 'primary.main'
              : 'divider')
            : 'divider',
        }}
      >
        <CardContent sx={{ pb: 1 }}>
          {/* Project header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                {project.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {project.client_name}
                {project.primary_job_number && ` · #${project.primary_job_number}`}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
              {project.current_phase && (
                <Chip label={project.current_phase} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
              )}
              {report && (
                <Chip
                  label={STATUS_LABELS[report.status]}
                  color={statusColor}
                  size="small"
                />
              )}
            </Box>
          </Box>

          {/* Progress bar if report exists */}
          {report && report.percent_complete != null && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary">Progress</Typography>
                <Typography variant="caption" color="text.secondary">{report.percent_complete}%</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={report.percent_complete}
                sx={{ height: 6, borderRadius: 3 }}
                color={report.percent_complete >= 100 ? 'success' : 'primary'}
              />
            </Box>
          )}
        </CardContent>

        <CardActions sx={{ pt: 0 }}>
          <Button
            fullWidth
            variant={expanded ? 'contained' : report ? 'outlined' : 'contained'}
            size="medium"
            onClick={() => setExpanded(!expanded)}
            startIcon={
              report
                ? report.status === 'submitted' || report.status === 'reviewed'
                  ? <VisibilityIcon />
                  : <EditIcon />
                : <EventNoteIcon />
            }
            sx={{
              minHeight: 48,
              bgcolor: expanded ? ACS_BLUE : undefined,
              color: expanded ? 'white' : undefined,
            }}
          >
            {expanded
              ? 'Collapse'
              : report
              ? report.status === 'draft'
                ? 'Edit Draft'
                : 'View Report'
              : 'Start Report'}
          </Button>
        </CardActions>

        {expanded && (
          <CardContent>
            <Divider sx={{ mb: 2 }} />
            <ReportForm
              project={project}
              existingReport={report}
              onSaved={handleSaved}
              onSubmitted={handleSubmitted}
            />
          </CardContent>
        )}
      </Card>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3000}
        onClose={() => setToast('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast('')}
          severity="success"
          sx={{ width: '100%' }}
        >
          {toast}
        </Alert>
      </Snackbar>
    </>
  );
}

// ── Management Dashboard ──────────────────────────────────────────────────────
function ManagementDashboard() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);
  const [reviewingId, setReviewingId] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  // Recent Submissions tab — added 2026-05-15 because today-only summary
  // looked broken to Tom/Robert/Jonathan whenever nobody had filed yet today.
  const [recent, setRecent] = useState(null);
  const [recentLoading, setRecentLoading] = useState(false);
  const [recentDays, setRecentDays] = useState(14);

  // Filter state for the Recent Submissions tab (Tom 2026-05-14 item #5).
  // Empty strings = no filter. Date range overrides the rolling "days" window
  // when either bound is set.
  const [filters, setFilters] = useState({
    pm_id: '',
    start: '',
    end: '',
    job_number: '',
    project_name: '',
    q: '',
  });
  // Debounced copy of the free-text filters — keystrokes don't hammer the API.
  const [debouncedTextFilters, setDebouncedTextFilters] = useState({
    job_number: '',
    project_name: '',
    q: '',
  });
  const [pmOptions, setPmOptions] = useState([]);

  // Load PM list once for the dropdown. Anyone with role/roles including
  // 'PM' or 'Management' can be a project PM.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getUsers();
        const all = res.data || [];
        const pms = all
          .filter((u) => {
            const roles = Array.isArray(u.roles) ? u.roles : (u.role ? [u.role] : []);
            return roles.some((r) => r === 'PM' || r === 'Management' || r === 'Sales');
          })
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        if (!cancelled) setPmOptions(pms);
      } catch (err) {
        console.error('Failed to load PM list:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Debounce free-text inputs (300ms).
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedTextFilters({
        job_number: filters.job_number,
        project_name: filters.project_name,
        q: filters.q,
      });
    }, 300);
    return () => clearTimeout(t);
  }, [filters.job_number, filters.project_name, filters.q]);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getDailyReportSummary({ date });
      setSummary(res.data);
    } catch (err) {
      setError('Failed to load summary');
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const loadRecent = useCallback(async () => {
    setRecentLoading(true);
    try {
      // Build query params — only include filters that are actually set.
      const params = { days: recentDays };
      if (filters.pm_id)                  params.pm_id = filters.pm_id;
      if (filters.start)                  params.start = filters.start;
      if (filters.end)                    params.end = filters.end;
      if (debouncedTextFilters.job_number)   params.job_number = debouncedTextFilters.job_number;
      if (debouncedTextFilters.project_name) params.project_name = debouncedTextFilters.project_name;
      if (debouncedTextFilters.q)            params.q = debouncedTextFilters.q;
      const res = await getDailyReportsRecent(params);
      setRecent(res.data);
    } catch (err) {
      // Non-fatal — the tab stays empty.
      console.error('loadRecent error:', err);
      setRecent({ days: recentDays, count: 0, reports: [] });
    } finally {
      setRecentLoading(false);
    }
  }, [recentDays, filters.pm_id, filters.start, filters.end,
      debouncedTextFilters.job_number, debouncedTextFilters.project_name,
      debouncedTextFilters.q]);

  // Lazy-load the Recent tab the first time it's opened, and refresh whenever
  // the days/filter state changes.
  useEffect(() => {
    if (tab === 3) loadRecent();
  }, [tab, loadRecent]);

  const clearFilters = () => {
    setFilters({ pm_id: '', start: '', end: '', job_number: '', project_name: '', q: '' });
  };
  const hasActiveFilters = Boolean(
    filters.pm_id || filters.start || filters.end ||
    filters.job_number || filters.project_name || filters.q
  );

  const handleReview = async (reportId) => {
    setReviewingId(reportId);
    try {
      await reviewDailyReport(reportId);
      await loadSummary();
    } catch (err) {
      console.error('Review error:', err);
    } finally {
      setReviewingId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
  }

  const submittedPct = summary?.total_staff
    ? Math.round((summary.submitted_count / summary.total_staff) * 100)
    : 0;

  return (
    <Box>
      {/* Date picker + refresh */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
        <TextField
          type="date"
          label="Report Date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          size="small"
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ minWidth: 180 }}
        />
        <Tooltip title="Refresh">
          <IconButton onClick={loadSummary} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Stat cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: ACS_BLUE, color: 'white' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" fontWeight={700}>{summary?.submitted_count ?? 0}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>Reports Submitted</Typography>
              <LinearProgress
                variant="determinate"
                value={submittedPct}
                sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.3)', '& .MuiLinearProgress-bar': { bgcolor: '#4CAF50' } }}
              />
              <Typography variant="caption" sx={{ opacity: 0.75 }}>
                {submittedPct}% of {summary?.total_staff} field staff
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4}>
          <Card sx={{ border: '2px solid', borderColor: 'warning.main' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" fontWeight={700} color="warning.main">
                {summary?.not_submitted_count ?? 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">Not Yet Submitted</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" fontWeight={700}>
                {summary?.by_project?.length ?? 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">Projects Active Today</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Submitted Today (${summary?.submitted_count ?? 0})`} />
        <Tab label={`Not Submitted (${summary?.not_submitted_count ?? 0})`} />
        <Tab label={`By Project (${summary?.by_project?.length ?? 0})`} />
        <Tab label={`Recent Submissions${recent?.count != null ? ` (${recent.count})` : ''}`} />
      </Tabs>

      {/* Tab 0: Submitted reports */}
      {tab === 0 && (
        <Box>
          {(summary?.submitted || []).length === 0 ? (
            <Alert severity="info">No reports submitted yet for {date}.</Alert>
          ) : (
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Technician</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell align="center">Hours</TableCell>
                    <TableCell align="center">% Complete</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(summary?.submitted || []).map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 28, height: 28, bgcolor: ACS_BLUE, fontSize: '0.75rem' }}>
                            {r.user_name?.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>{r.user_name}</Typography>
                            <Typography variant="caption" color="text.secondary">{r.user_role}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{r.project_name}</Typography>
                        {r.job_number && (
                          <Typography variant="caption" color="text.secondary">#{r.job_number}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">{r.hours_worked}h</TableCell>
                      <TableCell align="center">
                        {r.percent_complete != null ? (
                          <Box>
                            <LinearProgress
                              variant="determinate"
                              value={r.percent_complete}
                              sx={{ height: 6, borderRadius: 3, width: 60, display: 'inline-flex' }}
                            />
                            <Typography variant="caption" display="block">{r.percent_complete}%</Typography>
                          </Box>
                        ) : '—'}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={STATUS_LABELS[r.status]}
                          color={STATUS_COLORS[r.status]}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {r.status === 'submitted' && (
                          <Tooltip title="Mark Reviewed">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleReview(r.id)}
                              disabled={reviewingId === r.id}
                            >
                              {reviewingId === r.id ? <CircularProgress size={16} /> : <ReviewIcon />}
                            </IconButton>
                          </Tooltip>
                        )}
                        {r.status === 'reviewed' && (
                          <CheckCircleIcon color="success" fontSize="small" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </Box>
      )}

      {/* Tab 1: Not submitted */}
      {tab === 1 && (
        <Box>
          {(summary?.not_submitted || []).length === 0 ? (
            <Alert severity="success">All field staff have submitted reports!</Alert>
          ) : (
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Technician</TableCell>
                    <TableCell>Role</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(summary?.not_submitted || []).map((u) => (
                    <TableRow key={u.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 28, height: 28, bgcolor: '#f57c00', fontSize: '0.75rem' }}>
                            <PersonOffIcon fontSize="small" />
                          </Avatar>
                          <Typography variant="body2">{u.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{u.role}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </Box>
      )}

      {/* Tab 2: By project */}
      {tab === 2 && (
        <Box>
          {(summary?.by_project || []).length === 0 ? (
            <Alert severity="info">No project activity for {date}.</Alert>
          ) : (
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Project</TableCell>
                    <TableCell align="center">Reports</TableCell>
                    <TableCell align="center">Avg % Complete</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(summary?.by_project || []).map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{p.project_name}</Typography>
                        {p.job_number && (
                          <Typography variant="caption" color="text.secondary">#{p.job_number}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">{p.report_count}</TableCell>
                      <TableCell align="center">
                        {p.avg_percent_complete != null
                          ? `${Math.round(p.avg_percent_complete)}%`
                          : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </Box>
      )}
      {/* Tab 3: Recent Submissions — last N days across all field staff */}
      {tab === 3 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 140 }} disabled={Boolean(filters.start || filters.end)}>
              <InputLabel>Window</InputLabel>
              <Select
                value={recentDays}
                label="Window"
                onChange={(e) => setRecentDays(e.target.value)}
              >
                <MenuItem value={7}>Last 7 days</MenuItem>
                <MenuItem value={14}>Last 14 days</MenuItem>
                <MenuItem value={30}>Last 30 days</MenuItem>
                <MenuItem value={60}>Last 60 days</MenuItem>
                <MenuItem value={90}>Last 90 days</MenuItem>
                <MenuItem value={180}>Last 180 days</MenuItem>
                <MenuItem value={365}>Last 365 days</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title="Refresh">
              <IconButton onClick={loadRecent} size="small" disabled={recentLoading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              {recentLoading ? 'Loading…' : `${recent?.count ?? 0} submission${recent?.count === 1 ? '' : 's'}`}
            </Typography>
          </Box>

          {/* Filter row — added 2026-05-20 (Tom item #5).
              Date range overrides the rolling "Window" when either bound is set. */}
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50' }}>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <FilterAltIcon fontSize="small" color="action" />
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>PM / Sales</InputLabel>
                <Select
                  value={filters.pm_id}
                  label="PM / Sales"
                  onChange={(e) => setFilters((f) => ({ ...f, pm_id: e.target.value }))}
                >
                  <MenuItem value=""><em>All PMs</em></MenuItem>
                  {pmOptions.map((u) => (
                    <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                type="date"
                label="Start"
                size="small"
                value={filters.start}
                onChange={(e) => setFilters((f) => ({ ...f, start: e.target.value }))}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ minWidth: 150 }}
              />
              <TextField
                type="date"
                label="End"
                size="small"
                value={filters.end}
                onChange={(e) => setFilters((f) => ({ ...f, end: e.target.value }))}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ minWidth: 150 }}
              />
              <TextField
                label="Job #"
                size="small"
                value={filters.job_number}
                onChange={(e) => setFilters((f) => ({ ...f, job_number: e.target.value }))}
                placeholder="24-0527"
                sx={{ width: 140 }}
              />
              <TextField
                label="Project name"
                size="small"
                value={filters.project_name}
                onChange={(e) => setFilters((f) => ({ ...f, project_name: e.target.value }))}
                placeholder="Beacon Court"
                sx={{ minWidth: 200 }}
              />
              <TextField
                label="Search in report body"
                size="small"
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                placeholder="keyword in tasks, blockers, materials…"
                sx={{ flexGrow: 1, minWidth: 220 }}
              />
              {hasActiveFilters && (
                <Tooltip title="Clear all filters">
                  <IconButton onClick={clearFilters} size="small">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Paper>

          {recentLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!recentLoading && (recent?.reports || []).length === 0 && (
            <Alert severity="info">
              {hasActiveFilters
                ? 'No daily field reports match the current filters. Try widening the date range, clearing filters, or adjusting your search.'
                : `No daily field reports submitted in the last ${recentDays} days. The feature is wired up end-to-end — once field staff start filing, their submissions will appear here for Management review.`}
            </Alert>
          )}

          {!recentLoading && (recent?.reports || []).length > 0 && (
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Technician</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell align="center">Hours</TableCell>
                    <TableCell align="center">% Complete</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(recent?.reports || []).map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(r.report_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 28, height: 28, bgcolor: ACS_BLUE, fontSize: '0.75rem' }}>
                            {r.user_name?.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>{r.user_name}</Typography>
                            <Typography variant="caption" color="text.secondary">{r.user_role}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{r.project_name}</Typography>
                        {r.job_number && (
                          <Typography variant="caption" color="text.secondary">#{r.job_number}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">{r.hours_worked != null ? `${r.hours_worked}h` : '—'}</TableCell>
                      <TableCell align="center">
                        {r.percent_complete != null ? `${r.percent_complete}%` : '—'}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={STATUS_LABELS[r.status]}
                          color={STATUS_COLORS[r.status]}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {r.status === 'submitted' && (
                          <Tooltip title="Mark Reviewed">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={async () => { await handleReview(r.id); loadRecent(); }}
                              disabled={reviewingId === r.id}
                            >
                              {reviewingId === r.id ? <CircularProgress size={16} /> : <ReviewIcon />}
                            </IconButton>
                          </Tooltip>
                        )}
                        {r.status === 'reviewed' && (
                          <CheckCircleIcon color="success" fontSize="small" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </Box>
      )}

    </Box>
  );
}

// ── Field Staff View ──────────────────────────────────────────────────────────
function FieldStaffView() {
  const [projects, setProjects] = useState([]);
  const [reports, setReports] = useState({}); // keyed by project_id
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        // Scoped project list: /api/daily-reports/my-projects returns only
        // projects relevant to this user (PM: assigned; field tech: dispatched
        // last 7 days; management: dispatch activity in last 14 days).
        // Tom Clancy 2026-05-15: was calling getProjects() which returned ALL
        // 237 approved projects to every user (Vinnie Baus reproduction).
        const [projRes, reportRes] = await Promise.all([
          getDailyReportMyProjects(),
          getDailyReports({ date: new Date().toISOString().split('T')[0] }),
        ]);

        setProjects(projRes.data || []);

        // Build a map of project_id → report
        const map = {};
        (reportRes.data || []).forEach((r) => {
          map[r.project_id] = r;
        });
        setReports(map);
      } catch (err) {
        setError('Failed to load projects');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleReportChange = (report) => {
    setReports((prev) => ({ ...prev, [report.project_id]: report }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
  }

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  const submittedToday = Object.values(reports).filter(
    (r) => r.status === 'submitted' || r.status === 'reviewed'
  ).length;

  return (
    <Box>
      {/* Today's date + quick stats */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${ACS_BLUE} 0%, #2a5298 100%)`,
          color: 'white',
          borderRadius: 2,
          p: 2,
          mb: 3,
        }}
      >
        <Typography variant="h6" fontWeight={700}>{today}</Typography>
        <Typography variant="body2" sx={{ opacity: 0.85 }}>
          {submittedToday} of {projects.length} project reports submitted
        </Typography>
        {projects.length > 0 && (
          <LinearProgress
            variant="determinate"
            value={projects.length ? (submittedToday / projects.length) * 100 : 0}
            sx={{
              mt: 1,
              height: 6,
              borderRadius: 3,
              bgcolor: 'rgba(255,255,255,0.3)',
              '& .MuiLinearProgress-bar': { bgcolor: '#4CAF50' },
            }}
          />
        )}
      </Box>

      {projects.length === 0 && (
        <Alert severity="info">
          No active projects assigned. Contact your PM if you think this is incorrect.
        </Alert>
      )}

      {projects.map((project) => (
        <ProjectCard
          key={project.id}
          project={project}
          todayReport={reports[project.id] || null}
          onReportChange={handleReportChange}
        />
      ))}
    </Box>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function DailyReport() {
  const { user } = useAuth();
  const isMgmt = user?.role === 'Management' || (Array.isArray(user?.roles) && user.roles.includes('Management'));

  return (
    <Box
      sx={{
        maxWidth: 800,
        mx: 'auto',
        px: { xs: 0, sm: 1 },
      }}
    >
      {/* Page header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <EventNoteIcon sx={{ fontSize: 32, color: ACS_BLUE }} />
        <Box>
          <Typography variant="h5" fontWeight={700} color={ACS_BLUE}>
            Daily Field Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isMgmt ? 'Management Dashboard' : 'Log your work for today'}
          </Typography>
        </Box>
      </Box>

      {isMgmt ? <ManagementDashboard /> : <FieldStaffView />}
    </Box>
  );
}
